<script setup lang="ts">
// components
import AppBreadcrumbs from '~/components/redesign/AppBreadcrumbs.vue';
import ArticleMarkup from '~/components/redesign/ArticleMarkup.vue';
import CopyToClipboard from '~/components/redesign/CopyToClipboard.vue';
import Forms from '~/components/redesign/Forms';

// utils
import { isString } from '~/utils';
import get from 'lodash.get';

// constants
import { STATUS_CODES, PATHS } from '~/constants';
import { PostStatus } from '~/types';

// stores
import useUIStore from '~/stores/ui';

// composable
import { useDayjs } from '#dayjs';

const { href } = useRequestURL();
const {
  params: { slug },
  query: { preview },
} = useRoute();
const dayjs = useDayjs();

const { setLoading } = useUIStore();

// state
const submitted = ref(false);
const onSubmitted = (value: boolean) => {
  submitted.value = value;
  setLoading(false);
};

const { error, data: article } = await useAsyncGql({
  operation: 'JobPost',
  variables: {
    slug: slug as string,
    statuses: preview
      ? [PostStatus.DRAFT, PostStatus.SCHEDULED]
      : [PostStatus.PUBLISHED],
  },
  options: {
    transform: response => get(response, 'job_post'),
  },
});

if (error.value || !article.value) {
  navigateTo(
    { path: PATHS.CAREERS.path },
    { redirectCode: STATUS_CODES.MOVED_PERMANENTLY },
  );
}

const title = get(article.value, 'title');
const markup = get(article.value, 'description');
const locations = get(article.value, 'location', []);
const applicationId = get(article.value, 'id');
const publishedAt = useState('publish-at', () =>
  get(article.value, 'publish-at'),
);

const formattedPublishedAt = dayjs(publishedAt.value).format('MMM Do YYYY');
const transformLocation = (value: string[]) => value.join(', ');

useSeoMeta({
  title: get(article.value, 'seo.title'),
  ogTitle: get(article.value, 'seo.title'),
  description: get(article.value, 'seo.description'),
  ogDescription: get(article.value, 'seo.description'),
  ogType: get(article.value, 'seo.og_type'),
  articlePublishedTime: publishedAt,
  ogUrl: href,
});

useHead({ meta: [{ name: 'robots', content: 'noindex' }] });
</script>

<template>
  <article data-page-name="careers-article">
    <div class="external-wrapper article">
      <AppBreadcrumbs v-if="isString(title)" :append="[title]" accent="light" />

      <div class="middle-wrapper">
        <h1 v-if="isString(title)" class="title">
          {{ title }}
        </h1>

        <strong v-if="locations.length" class="subtitle">
          {{ transformLocation(locations) }}
        </strong>

        <div class="meta">
          <time
            :datetime="publishedAt"
            :title="formattedPublishedAt"
            pubdate
            class="left"
          >
            {{ formattedPublishedAt }}
          </time>

          <span class="right">
            <strong>{{ $t('Share') }}:</strong>

            <CopyToClipboard :source="href" />
          </span>
        </div>
      </div>

      <ArticleMarkup v-if="isString(markup)" :markup />

      <Forms.Wrapper>
        <transition name="fade" mode="out-in">
          <div v-if="submitted" class="thank-you">
            <strong v-html="$t('careers-slug.thank-you.title')" />
            <p v-html="$t('careers-slug.thank-you.description')" />
          </div>

          <div v-else class="wrapper">
            <h2>{{ $t('careers-slug.form.title') }}</h2>

            <Forms.ApplyRole
              :btn-text="$t('careers-slug.form.title')"
              :default-position="title"
              :application-id
              accent="purple"
              @loading="setLoading"
              @submitted="onSubmitted(true)"
            />
          </div>
        </transition>
      </Forms.Wrapper>
    </div>
  </article>
</template>

<style scoped lang="scss">
@use '_/mixins/size';
@use '_/mixins/flex';
@use '_/mixins/typo';
@use '_/mixins/media';
@use '_/fn';

.external-wrapper {
  padding: 0 2rem;
  @include size.wrapper(80rem);

  @include media.mobile {
    padding: 0 1rem;
  }
}

[data-page-name='careers-article'] {
  padding-top: 2rem;

  @include media.tablet {
    padding-top: 1.5rem;
  }

  .article {
    [data-component-name='AppBreadcrumbs'] {
      align-self: flex-start;
      margin-bottom: 3rem;

      @include media.tablet {
        margin-bottom: 2rem;
      }

      @include media.mobile {
        margin-bottom: 1.5rem;
      }
    }

    .middle-wrapper {
      @include size.wrapper(50rem);

      @include flex.center-start;
      flex-direction: column;
      margin-bottom: 2rem;

      @include media.tablet {
        @include size.wrapper(100%);
        margin-bottom: 1.5rem;
      }

      @include media.mobile {
        margin-bottom: 0.5rem;
      }

      h1.title {
        @include typo.h3;
        text-align: center;
        margin-bottom: 0.5rem;
      }

      strong.subtitle {
        @include typo.subtitle-1;
        margin-bottom: 1rem;

        @include media.mobile {
          margin-bottom: 0.5rem;
        }
      }

      .meta {
        align-self: stretch;
        @include flex.center-sb;

        padding: 1rem 0;
        border-bottom: 1px solid fn.token('outline-secondary');

        @include media.mobile {
          @include flex.center-stretch;
          flex-direction: column;

          gap: 0.5rem 0;
        }

        time {
          @include typo.body-4;
          color: fn.token('text-secondary');
        }

        .right {
          @include flex.center-center;
          gap: 0 0.5rem;

          @include media.tablet {
            margin-right: 1rem;
          }

          @include media.mobile {
            @include flex.center-stretch;
            margin-right: 0;
          }

          strong {
            @include typo.subtitle-2;
            color: fn.token('text-secondary');
          }
        }
      }
    }

    [data-component-name='ArticleMarkup'] {
      @include size.wrapper(37rem);
      margin-bottom: 2rem;

      @include media.tablet {
        @include size.wrapper(44rem);
        margin-bottom: 1.5rem;
      }
    }

    [data-component-name='Forms.Wrapper'] {
      @include size.wrapper(37rem);

      margin-bottom: 8rem;
      padding: 2rem;

      @include media.tablet {
        margin-bottom: 8.75rem;
        padding: 2rem 1.5rem;
      }

      @include media.mobile {
        margin-bottom: 5rem;
        padding: 1.5rem 1rem;
      }

      .wrapper {
        h2 {
          @include typo.h4;
          margin-bottom: 2rem;
        }
      }

      .thank-you {
        min-height: 56.125rem;

        background-color: fn.token('surf-cont-primary');
        padding: 2rem;

        @include flex.center-center;
        flex-direction: column;
        gap: 1.5rem;

        text-align: center;

        strong {
          @include typo.subtitle-1;
        }

        p {
          @include typo.body-3;
        }
      }
    }
  }
}
</style>
